import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Stack,
  Text,
  useColorModeValue,
  VStack,
  SimpleGrid,
  Badge,
  Icon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Image,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  List,
  ListItem,
  ListIcon,
} from "@chakra-ui/react";
import { IoMdArrowForward } from "react-icons/io";
import {
  FaCheckCircle,
  FaShieldAlt,
  FaMicrophone,
  FaStethoscope,
  FaUserMd,
} from "react-icons/fa";

import IconBox from "../pure/Icons/IconBox";

import Footer from "../Common/Footer";
import Header from "../Common/Header";

import { handleCTA } from "../../utils";

// --- Mock Data / Constants for Medical Transcription ---

const TRANSCRIPTION_CHALLENGES = [
  {
    title: "Voice Capture",
    description:
      "Accurately capture every word in busy clinical environments with numerous distractions and background noise.",
    icon: FaMicrophone,
  },
  {
    title: "Medical Terminology",
    description:
      "Correctly transcribe thousands of complex medical terms, medications, procedures, and diagnoses.",
    icon: FaStethoscope,
  },
  {
    title: "Multiple Speakers",
    description:
      "Distinguish between doctors, patients, and family members in the same conversation with varying accents and speech patterns.",
    icon: FaUserMd,
  },
];

const FEATURE_TABS = [
  {
    title: "Real-Time Transcription",
    content:
      "Our AI captures every word as it's spoken with sub-second latency. Doctors can maintain natural conversation flow while the system works silently in the background, ensuring no critical information is missed.",
    image: "/assets/images/ambient-doc.gif",
  },
  {
    title: "Medical Terminology Mastery",
    content:
      "Our specialized medical language model recognizes over 30,000 medical terms, medications, and procedures with unparalleled accuracy. The system differentiates between similar-sounding terms like 'dysphagia' and 'dysphasia' that have entirely different clinical meanings.",
    image: "/assets/images/smart-templates.gif",
  },
  {
    title: "Multi-Speaker Recognition",
    content:
      "Advanced voice identification technology distinguishes between doctors, patients, family members, and other healthcare professionals in the same conversation. The system handles diverse accents, speech patterns, and languages.",
    image: "/assets/images/clinical-context.gif",
  },
];

const SIMILAR_TERMS = [
  {
    term1: "Dysphagia",
    meaning1: "Difficulty swallowing",
    term2: "Dysphasia",
    meaning2: "Difficulty speaking",
    impact: "Different specialists needed",
  },
  {
    term1: "Hyperglycemia",
    meaning1: "High blood sugar",
    term2: "Hypoglycemia",
    meaning2: "Low blood sugar",
    impact: "Opposite treatments required",
  },
  {
    term1: "Hydration",
    meaning1: "Fluid balance",
    term2: "Hydatid",
    meaning2: "Parasitic cyst",
    impact: "Completely different conditions",
  },
  {
    term1: "Lamictal",
    meaning1: "Epilepsy medication",
    term2: "Lamisil",
    meaning2: "Antifungal medication",
    impact: "Different drug classes and effects",
  },
];

const KEY_BENEFITS = [
  {
    stat: "60%",
    label: "Reduction in Documentation Time",
    description:
      "Physicians save hours daily, allowing more time for patient care and reducing burnout.",
  },
  {
    stat: "98%",
    label: "Medical Terminology Accuracy",
    description:
      "Our specialized AI accurately captures complex medical terms and distinguishes similar-sounding words.",
  },
  {
    stat: "45%",
    label: "Increase in Patient Satisfaction",
    description:
      "Better doctor-patient engagement through increased eye contact and active listening.",
  },
];

// --- Component Implementation ---

function MedicalTranscription() {
  const heroButtonBg = useColorModeValue("white", "gray.800");
  const bgGradient = "linear(to-r, purple.400, blue.500)"; // Different gradient for visual distinction
  const sectionBg = useColorModeValue("gray.50", "gray.900");
  const featureBg = useColorModeValue("white", "gray.800");
  const statColor = useColorModeValue("purple.500", "purple.300");
  const patternBg = useColorModeValue(
    "url('/assets/patterns/pattern-circles.svg')",
    "url('/assets/patterns/dark-pattern-circles.svg')"
  );

  return (
    <Box>
      {/* Use Minimal Header for Topic Pages */}
      <Header minimal={false} />

      {/* Hero Section - Distinct for Medical Transcription */}
      <Box
        as="section"
        bgGradient={bgGradient}
        position="relative"
        overflow="hidden"
        py={{ base: 20, md: 28 }}
        textAlign="center"
      >
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.08}
          backgroundImage={patternBg}
          backgroundSize="cover"
        />
        <Container maxW="container.lg" position="relative">
          <VStack spacing={6} align="center">
            <Badge
              px={4}
              py={1}
              bg="rgba(255,255,255,0.15)"
              color="white"
              fontSize="md"
              borderRadius="full"
              fontWeight="semibold"
            >
              AI-Powered Medical Transcription
            </Badge>
            <Heading
              size="2xl"
              lineHeight="1.2"
              fontWeight="bold"
              color="white"
              maxW="container.md"
            >
              From Words To Clinical Insight: Transcription Reimagined
            </Heading>
            <Text fontSize="xl" color="whiteAlpha.900" maxW="container.md">
              Experience medical transcription that actually understands
              healthcare. QIQ's specialized AI captures every nuance of clinical
              conversations, freeing physicians to focus on what matters most:
              their patients.
            </Text>
            <HStack spacing={4} pt={4}>
              <Button
                size="lg"
                bg={heroButtonBg}
                color="purple.500"
                _hover={{ bg: "white", transform: "translateY(-2px)" }}
                borderRadius="full"
                px={8}
                fontWeight="bold"
                boxShadow="lg"
                rightIcon={<IoMdArrowForward />}
                onClick={() => handleCTA("signup")}
                transition="all 0.3s"
              >
                See It In Action
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="whiteAlpha.400"
                borderRadius="full"
                px={8}
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
                fontWeight="medium"
                onClick={() => {
                  const featureSection = document.getElementById("challenges");
                  featureSection?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Explore Challenges
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* The Challenge Section - With Clinical Scenario */}
      <Box as="section" py={20} bg={featureBg}>
        <Container maxW="container.lg">
          <Stack
            direction={{ base: "column", lg: "row" }}
            spacing={10}
            align="center"
          >
            <Box flex="1">
              <Text
                color="purple.500"
                fontWeight="semibold"
                fontSize="lg"
                mb={3}
                letterSpacing="wide"
                textTransform="uppercase"
              >
                The Reality
              </Text>
              <Heading
                size="xl"
                mb={4}
                fontWeight="bold"
                color={useColorModeValue("gray.700", "white")}
              >
                A Tale of Two Emergency Rooms
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
                mb={4}
              >
                Imagine a critical patient encounter at 2AM in a bustling trauma
                center:
              </Text>
              <Box
                p={6}
                bg={sectionBg}
                borderRadius="md"
                borderLeftWidth="4px"
                borderLeftColor="red.500"
                mb={6}
              >
                <Text fontSize="md">
                  <Text as="span" fontWeight="bold">
                    Without AI transcription:
                  </Text>{" "}
                  The physician types frantically while trying to maintain eye
                  contact. Critical details are missed. "Dysphagia" becomes
                  "dysphasia" in the notes - two entirely different conditions
                  requiring different specialists. The doctor spends 15 minutes
                  after the encounter correcting errors.
                </Text>
              </Box>
              <Box
                p={6}
                bg={sectionBg}
                borderRadius="md"
                borderLeftWidth="4px"
                borderLeftColor="green.500"
              >
                <Text fontSize="md">
                  <Text as="span" fontWeight="bold">
                    With QIQ transcription:
                  </Text>{" "}
                  The doctor maintains complete focus on the patient. The AI
                  captures every word with perfect accuracy - even
                  distinguishing between similar medical terms. Documentation is
                  complete and accurate the moment the conversation ends. The
                  physician immediately moves to the next patient without
                  documentation lag.
                </Text>
              </Box>
            </Box>
            <Box
              flex="1"
              maxW={{ base: "100%", lg: "450px" }}
              borderRadius="lg"
              overflow="hidden"
              boxShadow="xl"
            >
              <Image
                src="/assets/images/doctor-patient.jpg"
                alt="Doctor-patient interaction"
                borderRadius="lg"
                w="full"
                h="auto"
              />
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Challenges Section */}
      <Box as="section" py={20} id="challenges" bg={sectionBg}>
        <Container maxW="container.xl">
          <VStack spacing={16}>
            <Box textAlign="center" maxW="container.md" mx="auto">
              <Text
                color="purple.500"
                fontWeight="semibold"
                fontSize="lg"
                mb={3}
                letterSpacing="wide"
                textTransform="uppercase"
              >
                The Transcription Challenge
              </Text>
              <Heading
                size="xl"
                mb={4}
                fontWeight="bold"
                color={useColorModeValue("gray.700", "white")}
              >
                Why Healthcare Needs Specialized Transcription
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
              >
                Medical conversations present unique challenges that general
                transcription AI simply can't handle.
              </Text>
            </Box>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} w="full">
              {TRANSCRIPTION_CHALLENGES.map((challenge, idx) => (
                <VStack
                  key={idx}
                  bg={featureBg}
                  borderRadius="xl"
                  boxShadow="md"
                  p={8}
                  spacing={4}
                  align="center"
                  textAlign="center"
                  transition="all 0.3s"
                  _hover={{ transform: "translateY(-5px)", boxShadow: "lg" }}
                >
                  <IconBox
                    h="60px"
                    w="60px"
                    bg="purple.50"
                    borderRadius="lg"
                    color="purple.500"
                  >
                    <Icon as={challenge.icon} w={7} h={7} />
                  </IconBox>
                  <Heading
                    size="md"
                    color={useColorModeValue("gray.700", "white")}
                  >
                    {challenge.title}
                  </Heading>
                  <Text
                    fontSize="md"
                    color={useColorModeValue("gray.600", "gray.400")}
                  >
                    {challenge.description}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* Similar Terms Section - Medical Complexity */}
      <Box as="section" py={20} bg={featureBg}>
        <Container maxW="container.lg">
          <VStack spacing={10} align="start">
            <Box maxW="container.md">
              <Text
                color="purple.500"
                fontWeight="semibold"
                fontSize="lg"
                mb={3}
                letterSpacing="wide"
                textTransform="uppercase"
              >
                The Complexity
              </Text>
              <Heading
                size="xl"
                mb={4}
                fontWeight="bold"
                color={useColorModeValue("gray.700", "white")}
              >
                When Words Sound the Same But Mean Something Entirely Different
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
                mb={6}
              >
                In healthcare, similar-sounding terms can have dramatically
                different meanings. Accuracy isn't just about convenience—it can
                impact patient safety and treatment decisions.
              </Text>
            </Box>

            <Box
              w="full"
              overflowX="auto"
              bg={sectionBg}
              p={6}
              borderRadius="lg"
              boxShadow="md"
            >
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Term</Th>
                    <Th>Meaning</Th>
                    <Th>Similar Term</Th>
                    <Th>Different Meaning</Th>
                    <Th>Clinical Impact</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {SIMILAR_TERMS.map((item, idx) => (
                    <Tr key={idx}>
                      <Td fontWeight="bold" color="purple.500">
                        {item.term1}
                      </Td>
                      <Td>{item.meaning1}</Td>
                      <Td fontWeight="bold" color="blue.500">
                        {item.term2}
                      </Td>
                      <Td>{item.meaning2}</Td>
                      <Td>{item.impact}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>

            <Box p={6} bg="yellow.50" borderRadius="md" w="full">
              <Text fontWeight="medium" color="yellow.800">
                Our medical transcription AI is trained on millions of clinical
                conversations and consistently achieves 98% accuracy with these
                challenging medical terms—far outperforming general-purpose
                transcription systems.
              </Text>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Interactive Feature Showcase Section */}
      <Box as="section" py={20} bg={sectionBg}>
        <Container maxW="container.xl">
          <VStack spacing={16}>
            <Box textAlign="center" maxW="container.md" mx="auto">
              <Text
                color="purple.500"
                fontWeight="semibold"
                fontSize="lg"
                mb={3}
                letterSpacing="wide"
                textTransform="uppercase"
              >
                The Technology
              </Text>
              <Heading
                size="xl"
                mb={4}
                fontWeight="bold"
                color={useColorModeValue("gray.700", "white")}
              >
                How QIQ Transcription Works
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
              >
                Behind the scenes technology that powers accurate,
                lightning-fast medical transcription.
              </Text>
            </Box>

            <Tabs variant="soft-rounded" colorScheme="purple" w="full" isFitted>
              <TabList mb="1em">
                {FEATURE_TABS.map((tab, idx) => (
                  <Tab key={idx} fontWeight="semibold">
                    {tab.title}
                  </Tab>
                ))}
              </TabList>
              <TabPanels>
                {FEATURE_TABS.map((tab, idx) => (
                  <TabPanel
                    key={idx}
                    p={8}
                    bg={featureBg}
                    borderRadius="xl"
                    boxShadow="md"
                  >
                    <Stack
                      direction={{ base: "column", lg: "row" }}
                      spacing={8}
                      align="center"
                    >
                      <Box flex="1">
                        <Heading size="lg" mb={4}>
                          {tab.title}
                        </Heading>
                        <Text fontSize="lg">{tab.content}</Text>
                      </Box>
                      <Box flex="1" maxW="450px">
                        <Image
                          src={tab.image}
                          alt={`${tab.title} illustration`}
                          borderRadius="lg"
                          boxShadow="lg"
                        />
                      </Box>
                    </Stack>
                  </TabPanel>
                ))}
              </TabPanels>
            </Tabs>
          </VStack>
        </Container>
      </Box>

      {/* Key Benefits / Stats Section */}
      <Box as="section" py={20} bg={featureBg}>
        <Container maxW="container.lg">
          <VStack spacing={12}>
            <Box textAlign="center" maxW="container.md" mx="auto">
              <Heading
                size="xl"
                mb={4}
                fontWeight="bold"
                color={useColorModeValue("gray.700", "white")}
              >
                Transforming Care Through Better Documentation
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
              >
                Our medical transcription doesn't just save time - it
                fundamentally changes the doctor-patient relationship for the
                better.
              </Text>
            </Box>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} w="full">
              {KEY_BENEFITS.map((benefit, idx) => (
                <VStack
                  key={idx}
                  p={6}
                  bg={sectionBg}
                  borderRadius="lg"
                  boxShadow="md"
                  spacing={3}
                  align="center"
                  textAlign="center"
                >
                  <Text
                    fontSize="5xl"
                    fontWeight="bold"
                    color={statColor}
                    lineHeight="1"
                  >
                    {benefit.stat}
                  </Text>
                  <Text fontSize="lg" fontWeight="semibold">
                    {benefit.label}
                  </Text>
                  <Text color={useColorModeValue("gray.600", "gray.400")}>
                    {benefit.description}
                  </Text>
                </VStack>
              ))}
            </SimpleGrid>

            <Box w="full" mt={8} p={8} bg="purple.50" borderRadius="xl">
              <Heading size="lg" mb={6} color="gray.700" textAlign="center">
                The End Result: Better Patient Care
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                <Box>
                  <Heading size="md" mb={4} color="purple.600">
                    For Physicians
                  </Heading>
                  <List spacing={3}>
                    <ListItem display="flex">
                      <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                      <Text>
                        Reduce administrative burden, saving 2+ hours daily
                      </Text>
                    </ListItem>
                    <ListItem display="flex">
                      <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                      <Text>
                        Maintain eye contact and engagement with patients
                      </Text>
                    </ListItem>
                    <ListItem display="flex">
                      <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                      <Text>
                        Create more comprehensive, accurate documentation
                      </Text>
                    </ListItem>
                    <ListItem display="flex">
                      <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                      <Text>
                        Focus on clinical reasoning rather than typing
                      </Text>
                    </ListItem>
                  </List>
                </Box>
                <Box>
                  <Heading size="md" mb={4} color="purple.600">
                    For Patients
                  </Heading>
                  <List spacing={3}>
                    <ListItem display="flex">
                      <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                      <Text>
                        Receive undivided attention during consultations
                      </Text>
                    </ListItem>
                    <ListItem display="flex">
                      <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                      <Text>
                        Benefit from more accurate diagnostic processes
                      </Text>
                    </ListItem>
                    <ListItem display="flex">
                      <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                      <Text>
                        Experience shorter wait times as efficiency improves
                      </Text>
                    </ListItem>
                    <ListItem display="flex">
                      <ListIcon as={FaCheckCircle} color="green.500" mt={1} />
                      <Text>
                        Enjoy improved continuity of care across providers
                      </Text>
                    </ListItem>
                  </List>
                </Box>
              </SimpleGrid>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* Trust & Compliance Section */}
      <Box as="section" py={20} bg={sectionBg}>
        <Container maxW="container.lg">
          <Stack
            direction={{ base: "column", lg: "row" }}
            spacing={10}
            align="center"
            bg={featureBg}
            p={10}
            borderRadius="xl"
            boxShadow="lg"
          >
            <IconBox
              h="80px"
              w="80px"
              bg="purple.50"
              borderRadius="full"
              color="purple.500"
              display={{ base: "none", lg: "flex" }}
            >
              <Icon as={FaShieldAlt} w={10} h={10} />
            </IconBox>
            <Box flex="1">
              <Heading
                size="lg"
                mb={4}
                color={useColorModeValue("gray.700", "white")}
              >
                Security & Privacy By Design
              </Heading>
              <Text
                fontSize="lg"
                color={useColorModeValue("gray.600", "gray.400")}
                mb={6}
              >
                Our medical transcription system meets and exceeds the highest
                industry standards for data protection. Patient conversations
                are processed with end-to-end encryption and our systems are
                fully compliant with healthcare regulations.
              </Text>
              <HStack spacing={4}>
                <Badge
                  colorScheme="blue"
                  variant="solid"
                  px={3}
                  py={1}
                  borderRadius="md"
                >
                  HIPAA Compliant
                </Badge>
                <Badge
                  colorScheme="green"
                  variant="solid"
                  px={3}
                  py={1}
                  borderRadius="md"
                >
                  SOC 2 Type II
                </Badge>
                <Badge
                  colorScheme="purple"
                  variant="solid"
                  px={3}
                  py={1}
                  borderRadius="md"
                >
                  End-to-End Encryption
                </Badge>
              </HStack>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* Final CTA Section */}
      <Box as="section" py={20} bgGradient={bgGradient} position="relative">
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          opacity={0.1}
          backgroundImage={patternBg}
          backgroundSize="cover"
        />
        <Container maxW="container.lg" position="relative">
          <VStack spacing={8} textAlign="center">
            <Heading size="xl" color="white">
              Transform Your Clinical Documentation Today
            </Heading>
            <Text fontSize="lg" color="whiteAlpha.900" maxW="container.md">
              Join thousands of healthcare professionals who are reclaiming
              their time and improving patient care with QIQ's medical
              transcription technology.
            </Text>
            <Button
              size="lg"
              bg="white"
              color="purple.500"
              _hover={{ bg: "white", transform: "translateY(-2px)" }}
              borderRadius="full"
              px={10}
              py={7}
              fontWeight="bold"
              rightIcon={<IoMdArrowForward />}
              onClick={() => handleCTA("signup")}
              boxShadow="lg"
            >
              Schedule Your Demo
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
    </Box>
  );
}

export default MedicalTranscription;
